import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, RefreshCw, Copy, Check, ExternalLink } from "lucide-react";

/**
 * Dev-only SEO debug panel.
 *
 * Renders nothing in production builds. In development, it floats a small
 * collapsible panel in the bottom-left corner that lists every meta tag
 * relevant to SEO/social sharing currently present in <head>, plus the
 * canonical link and document title. Required fields that are missing or
 * fail format validation (e.g. non-HTTPS image URLs) are highlighted.
 */

type ValidatorResult = { ok: true } | { ok: false; reason: string };
type Validator = (value: string) => ValidatorResult;

type Field = {
  label: string;
  value: string | null;
  required: boolean;
  formatError: string | null;
};

type ImageCheckStatus = "idle" | "loading" | "pass" | "fail";
type ImageCheck = {
  status: ImageCheckStatus;
  width?: number;
  height?: number;
  reason?: string;
};

// Facebook's hard minimum is 200×200; recommended is 1200×630.
// We warn below recommended, fail below the absolute floor.
const OG_IMAGE_MIN_WIDTH = 600;
const OG_IMAGE_MIN_HEIGHT = 315;
const OG_IMAGE_RECOMMENDED_WIDTH = 1200;
const OG_IMAGE_RECOMMENDED_HEIGHT = 630;
const IMAGE_CHECK_LABELS = ["og:image", "og:image:secure_url", "twitter:image"] as const;
type ImageCheckLabel = (typeof IMAGE_CHECK_LABELS)[number];

type MetaSpec = {
  label: string;
  selector: string;
  required?: boolean;
  validate?: Validator;
};

const isNonEmpty: Validator = (v) =>
  v.trim().length > 0 ? { ok: true } : { ok: false, reason: "empty value" };

const isHttpsUrl: Validator = (v) => {
  const trimmed = v.trim();
  if (!trimmed) return { ok: false, reason: "empty value" };
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:") {
      return { ok: false, reason: `must be HTTPS (got ${url.protocol.replace(":", "")})` };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: "not a valid URL" };
  }
};

const isAbsoluteUrl: Validator = (v) => {
  const trimmed = v.trim();
  if (!trimmed) return { ok: false, reason: "empty value" };
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return { ok: false, reason: `unsupported protocol ${url.protocol.replace(":", "")}` };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: "not an absolute URL" };
  }
};

const isOneOf =
  (allowed: readonly string[]): Validator =>
  (v) => {
    const trimmed = v.trim().toLowerCase();
    return allowed.includes(trimmed)
      ? { ok: true }
      : { ok: false, reason: `expected one of ${allowed.join(", ")}` };
  };

const maxLength =
  (max: number): Validator =>
  (v) =>
    v.trim().length <= max
      ? { ok: true }
      : { ok: false, reason: `exceeds ${max} chars (${v.trim().length})` };

const composeValidators =
  (...validators: Validator[]): Validator =>
  (v) => {
    for (const validator of validators) {
      const result = validator(v);
      if (!result.ok) return result;
    }
    return { ok: true };
  };

const META: readonly MetaSpec[] = [
  { label: "title", selector: "__title__", required: true, validate: composeValidators(isNonEmpty, maxLength(70)) },
  { label: "description", selector: 'meta[name="description"]', required: true, validate: composeValidators(isNonEmpty, maxLength(200)) },
  { label: "canonical", selector: 'link[rel="canonical"]', required: true, validate: isAbsoluteUrl },
  { label: "og:title", selector: 'meta[property="og:title"]', required: true, validate: isNonEmpty },
  { label: "og:description", selector: 'meta[property="og:description"]', required: true, validate: isNonEmpty },
  { label: "og:type", selector: 'meta[property="og:type"]', required: true, validate: isOneOf(["website", "article", "profile", "book", "video.movie"]) },
  { label: "og:url", selector: 'meta[property="og:url"]', required: true, validate: isAbsoluteUrl },
  { label: "og:image", selector: 'meta[property="og:image"]', required: true, validate: isAbsoluteUrl },
  { label: "og:image:alt", selector: 'meta[property="og:image:alt"]', validate: isNonEmpty },
  { label: "og:image:secure_url", selector: 'meta[property="og:image:secure_url"]', required: true, validate: isHttpsUrl },
  { label: "og:site_name", selector: 'meta[property="og:site_name"]', validate: isNonEmpty },
  { label: "og:locale", selector: 'meta[property="og:locale"]', validate: isNonEmpty },
  { label: "twitter:card", selector: 'meta[name="twitter:card"]', required: true, validate: isOneOf(["summary", "summary_large_image", "app", "player"]) },
  { label: "twitter:title", selector: 'meta[name="twitter:title"]', required: true, validate: isNonEmpty },
  { label: "twitter:description", selector: 'meta[name="twitter:description"]', required: true, validate: isNonEmpty },
  { label: "twitter:image", selector: 'meta[name="twitter:image"]', required: true, validate: isHttpsUrl },
  { label: "twitter:image:alt", selector: 'meta[name="twitter:image:alt"]', validate: isNonEmpty },
  { label: "twitter:site", selector: 'meta[name="twitter:site"]', validate: isNonEmpty },
];

const readField = (selector: string): string | null => {
  if (selector === "__title__") return document.title || null;
  const el = document.head.querySelector(selector);
  if (!el) return null;
  if (el.tagName === "LINK") return el.getAttribute("href");
  return el.getAttribute("content");
};

const evaluateFields = (): Field[] =>
  META.map((m) => {
    const value = readField(m.selector);
    let formatError: string | null = null;
    if (value && m.validate) {
      const result = m.validate(value);
      if (result.ok === false) formatError = result.reason;
    }


    return {
      label: m.label,
      value,
      required: m.required ?? false,
      formatError,
    };
  });

const probeImage = (url: string): Promise<ImageCheck> =>
  new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      img.src = "";
      resolve({ status: "fail", reason: "load timeout (>8s)" });
    }, 8000);

    img.onload = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      if (!width || !height) {
        resolve({ status: "fail", reason: "image has no dimensions", width, height });
        return;
      }
      if (width < OG_IMAGE_MIN_WIDTH || height < OG_IMAGE_MIN_HEIGHT) {
        resolve({
          status: "fail",
          width,
          height,
          reason: `below minimum ${OG_IMAGE_MIN_WIDTH}×${OG_IMAGE_MIN_HEIGHT}`,
        });
        return;
      }
      if (width < OG_IMAGE_RECOMMENDED_WIDTH || height < OG_IMAGE_RECOMMENDED_HEIGHT) {
        resolve({
          status: "fail",
          width,
          height,
          reason: `below recommended ${OG_IMAGE_RECOMMENDED_WIDTH}×${OG_IMAGE_RECOMMENDED_HEIGHT}`,
        });
        return;
      }
      resolve({ status: "pass", width, height });
    };
    img.onerror = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve({ status: "fail", reason: "failed to load (network/CORS/404)" });
    };

    img.src = url;
  });

const SeoDebugPanel = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState<Field[]>([]);
  const [lastVerifiedAt, setLastVerifiedAt] = useState<Date | null>(null);
  const [verifyFlash, setVerifyFlash] = useState<"pass" | "fail" | null>(null);
  const [imageChecks, setImageChecks] = useState<Record<string, ImageCheck>>({});
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    const next = evaluateFields();
    setFields(next);
    return next;
  }, []);

  const runImageChecks = useCallback(async (currentFields: Field[]) => {
    const targets = currentFields.filter(
      (f): f is Field & { value: string } =>
        IMAGE_CHECK_LABELS.includes(f.label as ImageCheckLabel) &&
        !!f.value &&
        !f.formatError,
    );
    if (targets.length === 0) {
      setImageChecks({});
      return [] as Array<{ label: string; check: ImageCheck }>;
    }
    setImageChecks(
      Object.fromEntries(targets.map((t) => [t.label, { status: "loading" } as ImageCheck])),
    );
    const results = await Promise.all(
      targets.map(async (t) => ({ label: t.label, check: await probeImage(t.value) })),
    );
    setImageChecks(Object.fromEntries(results.map((r) => [r.label, r.check])));
    return results;
  }, []);

  const reverify = useCallback(async () => {
    const next = refresh();
    const metaFailing = next.filter(
      (f) => (f.required && !f.value) || (f.value && f.formatError),
    ).length;
    setLastVerifiedAt(new Date());
    setVerifyFlash(metaFailing === 0 ? "pass" : "fail");
    window.setTimeout(() => setVerifyFlash(null), 1600);
    const imageResults = await runImageChecks(next);
    const imageFailing = imageResults.filter((r) => r.check.status === "fail").length;
    const total = metaFailing + imageFailing;
    setVerifyFlash(total === 0 ? "pass" : "fail");
    window.setTimeout(() => setVerifyFlash(null), 1600);
  }, [refresh, runImageChecks]);

  useEffect(() => {
    // SEO component updates head in an effect — read on next tick.
    const id = window.setTimeout(() => {
      const next = refresh();
      void runImageChecks(next);
    }, 50);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search, refresh, runImageChecks]);

  if (!import.meta.env.DEV) return null;

  const missing = fields.filter((f) => f.required && !f.value);
  const formatIssues = fields.filter((f) => f.value && f.formatError);
  const imageFailures = Object.values(imageChecks).filter((c) => c.status === "fail").length;
  const imageLoading = Object.values(imageChecks).some((c) => c.status === "loading");
  const failingCount = missing.length + formatIssues.length + imageFailures;
  const ok = failingCount === 0 && !imageLoading;

  const statusLabel = ok
    ? "OK"
    : [
        missing.length ? `${missing.length} missing` : null,
        formatIssues.length ? `${formatIssues.length} invalid` : null,
        imageFailures ? `${imageFailures} image` : null,
      ]
        .filter(Boolean)
        .join(" · ");

  // Find the first image-tag field whose value failed validation (format or load).
  const firstFailingImage = fields.find((f) => {
    if (!IMAGE_CHECK_LABELS.includes(f.label as ImageCheckLabel)) return false;
    if (f.value && f.formatError) return true;
    const check = imageChecks[f.label];
    return !!f.value && check?.status === "fail";
  });
  const firstFailingReason = firstFailingImage
    ? firstFailingImage.formatError ??
      imageChecks[firstFailingImage.label]?.reason ??
      "invalid image"
    : null;

  const copyFailingUrl = async () => {
    if (!firstFailingImage?.value) return;
    try {
      await navigator.clipboard.writeText(firstFailingImage.value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable — silently ignore in dev panel.
    }
  };


  return (
    <div className="fixed bottom-24 left-4 z-[60] max-w-[min(92vw,420px)] font-mono text-[11px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 rounded-full px-3 py-1.5 shadow-lg backdrop-blur-sm border transition-colors ${
          ok
            ? "bg-background/90 border-border text-foreground"
            : "bg-destructive/90 border-destructive text-destructive-foreground"
        }`}
        aria-label="Toggle SEO debug panel"
      >
        {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
        <span className="font-semibold tracking-wider uppercase">
          SEO {ok ? "OK" : `· ${statusLabel}`}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      </button>

      {open && (
        <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-md border border-border bg-background/95 p-3 shadow-xl backdrop-blur-sm">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-muted-foreground">
              Route: <span className="text-foreground">{location.pathname}</span>
            </div>
            <button
              type="button"
              onClick={reverify}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                verifyFlash === "pass"
                  ? "border-green-600 bg-green-600/15 text-green-700 dark:text-green-400"
                  : verifyFlash === "fail"
                    ? "border-destructive bg-destructive/15 text-destructive"
                    : "border-border bg-muted/40 text-foreground hover:bg-muted"
              }`}
              aria-label="Re-verify meta tags"
            >
              <RefreshCw size={11} />
              {verifyFlash === "pass"
                ? "Pass"
                : verifyFlash === "fail"
                  ? `Fail (${failingCount})`
                  : "Re-verify"}
            </button>
          </div>
          {lastVerifiedAt && (
            <div className="mb-2 text-[10px] text-muted-foreground">
              Last verified {lastVerifiedAt.toLocaleTimeString()}
            </div>
          )}
          {firstFailingImage && firstFailingImage.value && (
            <div className="mb-3 rounded-md border-2 border-destructive bg-destructive/10 p-2 text-destructive">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[10px]">
                  <AlertTriangle size={11} />
                  Replace image: {firstFailingImage.label}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={copyFailingUrl}
                    className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                      copied
                        ? "border-green-600 bg-green-600/15 text-green-700 dark:text-green-400"
                        : "border-destructive bg-background/40 hover:bg-background/70"
                    }`}
                    aria-label="Copy failing image URL"
                  >
                    {copied ? <Check size={10} /> : <Copy size={10} />}
                    {copied ? "Copied" : "Copy URL"}
                  </button>
                  <a
                    href={firstFailingImage.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded-full border border-destructive bg-background/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors hover:bg-background/70"
                    aria-label="Open failing image in new tab"
                  >
                    <ExternalLink size={10} />
                    Open
                  </a>
                </div>
              </div>
              <div className="break-all text-[10px] text-foreground/90">
                {firstFailingImage.value}
              </div>
              {firstFailingReason && (
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider">
                  ⚠ {firstFailingReason}
                </div>
              )}
            </div>
          )}
          <ul className="space-y-1.5">
            {fields.map((f) => {
              const isMissingRequired = f.required && !f.value;
              const hasFormatError = !!(f.value && f.formatError);
              const imageCheck = imageChecks[f.label];
              const hasImageFail = imageCheck?.status === "fail";
              const isLoading = imageCheck?.status === "loading";
              const isFailing = isMissingRequired || hasFormatError || hasImageFail;
              return (
                <li
                  key={f.label}
                  className={`flex flex-col gap-0.5 rounded px-2 py-1 ${
                    isFailing
                      ? "bg-destructive/10 text-destructive"
                      : "bg-muted/40"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">
                      {f.label}
                      {f.required && <span className="opacity-60"> *</span>}
                    </span>
                    {isMissingRequired ? (
                      <span className="uppercase text-[9px] tracking-wider">
                        missing
                      </span>
                    ) : hasFormatError ? (
                      <span className="uppercase text-[9px] tracking-wider">
                        invalid
                      </span>
                    ) : hasImageFail ? (
                      <span className="uppercase text-[9px] tracking-wider">
                        image
                      </span>
                    ) : isLoading ? (
                      <span className="uppercase text-[9px] tracking-wider opacity-70">
                        checking…
                      </span>
                    ) : imageCheck?.status === "pass" ? (
                      <span className="uppercase text-[9px] tracking-wider text-green-700 dark:text-green-400">
                        {imageCheck.width}×{imageCheck.height}
                      </span>
                    ) : null}
                  </div>
                  <div className="break-all text-foreground/80">
                    {f.value || <span className="italic opacity-60">—</span>}
                  </div>
                  {hasFormatError && (
                    <div className="text-[10px] font-semibold uppercase tracking-wider">
                      ⚠ {f.formatError}
                    </div>
                  )}
                  {hasImageFail && (
                    <div className="text-[10px] font-semibold uppercase tracking-wider">
                      ⚠ {imageCheck.reason}
                      {imageCheck.width && imageCheck.height
                        ? ` (${imageCheck.width}×${imageCheck.height})`
                        : ""}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

        </div>
      )}
    </div>
  );
};

export default SeoDebugPanel;
